import { BaseListViewStore, HyperlinkCellData, ProgressBarCellData } from 'core/stores';
import { TableFilterTemplate } from 'themes/components';
import { isSuperAdminOrganization, isWarehouse, notificationError, TableFilterUtility } from 'core/utils';
import { observable, action, makeObservable } from 'mobx';
import { BaasicTableSettingsViewStore } from 'common/stores';
import { ReceivingStore } from 'modules/receiving/stores';
import { LookupStore } from 'modules/lookup/stores';
import { receivingStatus, lookupRouteName, sourceOfChange } from 'common/constants';
import { localStorageHandler } from 'core/services';
import { checkForColumnFilter, checkForColumnWidth, RemoveFiltersModalUtility } from 'core/utils';
import { ApplicationService } from 'common/data';
import _ from 'lodash';

class ReceivingAutomatedListViewStore extends BaseListViewStore {
	@observable change = false;
	selectedItem = null;
	localStorageWidthKey = 'receivings-automated-column-width';

	constructor(rootStore) {
		super(rootStore, {
			listStore: new ReceivingStore(rootStore),
			queryParams: {
				embed: [
					'receivingStatus',
					'receivingType',
					'warehouseTo',
					'warehouseFrom',
					'receivingProducts',
					'receivingProducts.product',
					'seller',
				],
				filter: localStorageHandler.get('receiving-automated-filter'),
			},
			tableData: {
				columns: [
					{
						key: 'ref',
						title: 'RECEIVINGVIEW.REF',
						dataCell: HyperlinkCellData,
						onClick: dataItem => this.routes.detail(dataItem.id),
						width: 200,
						selected: true,
						filter: null,
						locked: true,
						filterType: TableFilterTemplate,
						sortable: true,
						filterApiParams: {
							receivingType: 2,
						},
					},
					{
						key: 'load',
						title: 'RECEIVINGVIEW.LOAD',
						dataCell: ProgressBarCellData,
						width: 200,
						filter: null,
						ignoreOnExport: true,
						selected: false,
						sortable: true,
					},
					{
						key: 'cost',
						title: 'RECEIVINGVIEW.COST',
						width: 200,
						filter: null,
						selected: false,
						sortable: true,
					},
					{
						key: 'warehouseTo.applicationTitle',
						title: 'RECEIVINGVIEW.WAREHOUSETO',
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						useAlias: true,
						selected: false,
						sortable: true,
						dontSplit: true,
						service: 'applicationService',
						filterApiParams: {
							warehouseOnly: true,
						},
					},
					{
						key: 'warehouseFrom.applicationTitle',
						title: 'RECEIVINGVIEW.WAREHOUSEFROM',
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						selected: false,
						sortable: true,
						dontSplit: true,
						service: 'applicationService',
						filterApiParams: {
							warehouseOnly: true,
						},
					},
					{
						key: 'receivingStatus.name',
						title: 'RECEIVINGVIEW.STATUS',
						width: 200,
						filter: null,
						selected: false,
						filterType: TableFilterTemplate,
						sortable: true,
						lookupConstant: lookupRouteName.receivingStatus,
						filterApiParams: {
							ignoreSystemValues: false,
						},
					},
					{
						key: 'seller.applicationTitle',
						title: 'RECEIVINGVIEW.SELLER',
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						useAlias: true,
						enabled: isWarehouse(rootStore) || isSuperAdminOrganization(rootStore),
						selected: false,
						sortable: true,
						dontSplit: true,
						service: 'applicationService',
						filterApiParams: {
							sellerOnly: true,
						},
					},
				],
				sortObject: [
					{
						field: 'ref',
						dir: 'asc',
					},
				],
				actions: {
					onEdit: item => this.routes.edit(item.id),
					onDelete: item =>
						(item.receivingStatus.abrv == receivingStatus.arrivedAtPort ||
							item.receivingStatus.abrv == receivingStatus.inTransit) &&
						item.receivingProducts.some(x => x.isReceived === true)
							? this.rootStore.notificationStore.error('LOOKUPDELETEERROR.RECEIVINGPORT')
							: this.openConfirmationModal({
									onConfirm: async () => {
										await this.deleteReceiving();
									},
									message: ['LOOKUPDELETECONFIRM.RECEIVING', { name: item.ref }],
									data: item,
							  }),
					onApprove: async item => {
						this.openConfirmationModal({
							onConfirm: async () => {
								await this.approveReceivingAsync();
							},
							message: ['RECEIVINGVIEW.CONFIRMAPPROVAL', { ref: item.ref }],
							data: item,
						});
					},
				},
			},
			permissions: {
				onEdit: () => this.authStore.hasPermission('receivingSection.update'),
				onDelete: () => this.authStore.hasPermission('receivingSection.remove'),
				onApprove: item => {
					return (
						this.authStore.hasPermission('receivingSection.update') &&
						item.receivingStatus.abrv == 'awaiting%20approval'
					);
				},
			},
			routes: {
				edit: id =>
					this.rootStore.routerStore.goTo('master.app.main.receiving.edit', {
						id,
					}),
				detail: id =>
					this.rootStore.routerStore.goTo('master.app.main.receiving.detail', {
						id,
					}),
			},
		});
		makeObservable(this);
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.suspend();
		if (isSuperAdminOrganization(this.rootStore) || isWarehouse(this.rootStore)) {
			await this.rootStore.viewStore.handleSellerForChildViewStore(this);
		}
		await this.queryUtility.initialize(this.findContainers);
		checkForColumnFilter(localStorageHandler.get('receiving-automated-table-data'), this.tableData.columns);
		checkForColumnWidth(localStorageHandler.get(this.localStorageWidthKey), this.tableData.columns);
		this.columnFilterSetup = 'receiving-automated-table-data';
		this.hasPiiAccess = this.rootStore.authStore.hasPiiAccess;
		this.removeFiltersModal = new RemoveFiltersModalUtility({ viewStore: this });
		this.tableFilter = new TableFilterUtility({
			lookupStore: new LookupStore(this.rootStore),
			listStore: this.listStore,
			queryUtility: this.queryUtility,
			columns: this.tableData.columns,
			services: [this.listStore.service, new ApplicationService(this.rootStore.app.baasic.apiClient)],
		});
		this.setHeaderModes();
		this.setSearchVisible();
		this.setButtons();
		this.resume();
	};

	@action.bound setButtons() {
		this.setButtonComponent({
			buttons: [
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-trash spc--right--sml',
					onClick: () => {
						this.removeFiltersModal.handleRemoveFiltersButtonClick();
					},
					text: 'FILTER.REMOVEALL',
					disabled: !!(this.queryUtility.filter.advancedSearch === undefined),
				},
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-cog spc--right--sml',
					onClick: this.baasicTableSettingsViewStore.openTableSettingsModal,
					text: 'SETTINGS',
				},
			],
		});
	}

	@action.bound setAsChanged() {
		this.change = !this.change;
	}

	@action.bound
	deleteReceiving = async () => {
		this.suspend();
		const data = this.rootStore.viewStore.selectedConfirmModal.data;
		try {
			await this.listStore.deleteReceiving(data);
			this.rootStore.notificationStore.success(['LOOKUPDELETE.RECEIVING', { name: data.ref }]);
			await this.queryUtility.initialize(false);
		} catch (err) {
			if (err.data != null) {
				this.rootStore.notificationStore.error([err.data, { name: data.ref }]);
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}
		this.resume();
	};

	@action.bound
	findContainers = async () => {
		this.suspend();
		if (this.baasicTableSettingsViewStore == undefined) {
			this.baasicTableSettingsViewStore = new BaasicTableSettingsViewStore(this.rootStore);
		}
		await this.baasicTableSettingsViewStore.setTemplates(
			this.tableData,
			'ReceivingAutomatedTableSettings',
			this.setAsChanged,
			this.findContainers
		);
		this.queryUtility.setFilterParams(this, { receivingType: 2 }, this.tableFilter);
		await this.listStore.findReceiving(this.queryUtility.filter);
		if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
			await this.queryUtility.changePage(1);
		}
		this.queryUtility.handleResponse(this.listStore.response);
		_.forEach(this.listStore.response.item, item => {
			if (item.warehouseFromId === null && item.warehouseFromName) {
				item.warehouseFrom = {
					applicationTitle: item.warehouseFromName,
				};
			}
			if (item.warehouseToId === null && item.warehouseToName) {
				item.warehouseTo = {
					applicationTitle: item.warehouseToName,
				};
			}
		});
		this.queryUtility.setFilterPersistance(this, 'receiving-automated-filter', 'receiving-automated-table-data');
		this.resume();
	};

	@action.bound
	expandChange = evt => {
		evt.dataItem.expanded = evt.value;
		this.setAsChanged();
	};

	@action.bound
	approveReceivingAsync = async () => {
		this.suspend();
		const item = this.rootStore.viewStore.selectedConfirmModal.data;
		try {
			await this.listStore.approve(item.id, sourceOfChange.approveReceiving);
			this.rootStore.notificationStore.success(['RECEIVINGVIEW.APPROVESUCCESS', { ref: item.ref }]);
			await this.findContainers();
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};
}

export default ReceivingAutomatedListViewStore;



// WEBPACK FOOTER //
// ./src/modules/receiving/stores/automated/ReceivingAutomatedListViewStore.js